import React, { useState } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { Formik, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Candidato } from '../../../model/Candidato/Candidato';
import api from '../../../api';
import BackendErrorMessage from '../../../components/BackendErrorMessage';

// types/processo.ts
export interface Unidade {
    id: string;
    nome: string;
    clientId: string;
}

export interface Link {
    rel: string;
    href: string;
}

export interface Cronograma {
    id: string;
    atividades: any[]; // Você pode tipar melhor se souber a estrutura
    _links: Link[];
}

export interface Edital {
    id: string;
    nome: string;
    processoId: string;
    processoNome: string;
    candidatos: any[]; // Você pode tipar melhor se souber a estrutura
}

export interface Processo {
    id: string;
    nome: string;
    unidade: Unidade;
    identificador: string;
    responsavel: string;
    cronograma: Cronograma;
    salaEspera: any | null; // Você pode tipar melhor
    dataCriacao: string;
    status: string;
    _links: Link[];
    editais: Edital[];
    numeroCandidatos: number;
    finalizavel: boolean;
    convocacoesEnviadas: boolean;
    nomeEditavel: boolean;
    cronogramaDefinido: boolean;
    bancasTerminadas: boolean;
}

const candidatoSchemaEtapa1 = Yup.object().shape({
    candidatoEditais: Yup.array()
        .of(
            Yup.object().shape({
                editalId: Yup.string().required('Edital é obrigatório'),
                inscricao: Yup.string().required('Inscrição é obrigatória'),
            })
        )
        .min(1, 'Pelo menos um edital com inscrição é obrigatório')
        .required('Editais são obrigatórios'),
    nome: Yup.string().required('Nome é obrigatório'),
    email: Yup.string().email('E-mail inválido').nullable(),
    cpf: Yup.string()
        .matches(/^$|^\d{3}\.\d{3}\.\d{3}-\d{2}$|^\d{11}$/, 'CPF deve estar no formato 123.456.789-09 ou 12345678909')
        .nullable(),
    passaporte: Yup.string().nullable(),
    showResponsavel: Yup.boolean(),
    responsavel: Yup.object().when('showResponsavel', {
        is: true,
        then: () => Yup.object({
            nome: Yup.string().required('Nome do responsável é obrigatório'),
            email: Yup.string().email('E-mail do responsável inválido').nullable(),
            cpf: Yup.string()
                .matches(/^$|^\d{3}\.\d{3}\.\d{3}-\d{2}$|^\d{11}$/, 'CPF deve estar no formato 123.456.789-09 ou 12345678909')
                .nullable(),
            passaporte: Yup.string().nullable(),
            nomeSocial: Yup.string().nullable(),
        }).required(),
        otherwise: () => Yup.mixed().notRequired().nullable()
    })
}).test(
    'documento-obrigatorio',
    'Pelo menos um documento (CPF ou Passaporte do candidato ou responsável) deve ser informado.',
    function (value) {
        const v = value as CandidatoFormValues;
        const candidatoDoc = v?.cpf || v?.passaporte;
        const responsavelDoc = v?.showResponsavel && v?.responsavel ? (v.responsavel.cpf || v.responsavel.passaporte) : null;
        const hasResponsavelDoc = responsavelDoc && (responsavelDoc.trim() !== '');
        return !!(candidatoDoc || hasResponsavelDoc);
    }
);

const candidatoSchemaEtapa2 = Yup.object().shape({
    tipoBanca: Yup.string().required('Tipo de banca é obrigatório'),
    status: Yup.string().required('Status é obrigatório'),
});

interface Responsavel {
    nome?: string;
    nomeSocial?: string;
    cpf?: string;
    passaporte?: string;
    email?: string;
}

interface CandidatoEdital {
    inscricao: string;
    candidatoId?: string;
    editalId: string;
    nomeEdital: string;
}

interface CandidatoFormValues {
    candidatoEditais: CandidatoEdital[];
    nome: string;
    nomeSocial?: string;
    cpf?: string;
    passaporte?: string;
    email: string;
    autodeclaracaoRacial?: string;
    status?: string;
    tipoBanca?: string;
    presidenteBanca?: string;
    responsavel?: Responsavel;
    showResponsavel?: boolean;
}

interface AdicionarCandidatoModalProps {
    show: boolean;
    onHide: () => void;
    processoId: string;
    onCandidatoCriado?: (candidato: Candidato, showSuccessToast?: boolean) => void;
    processo: Processo
}

const AdicionarCandidatoModal: React.FC<AdicionarCandidatoModalProps> = ({
    show,
    onHide,
    processoId,
    onCandidatoCriado,
    processo
}) => {
    const [etapa, setEtapa] = useState(1);

    const initialValues: CandidatoFormValues = {
        candidatoEditais: [{ editalId: '', inscricao: '', nomeEdital: '' }],
        nome: '',
        nomeSocial: '',
        cpf: '',
        passaporte: '',
        email: '',
        autodeclaracaoRacial: '',
        status: '',
        tipoBanca: '',
        presidenteBanca: '',
        responsavel: undefined,
        showResponsavel: false,
    };

    const handleClose = () => {
        setEtapa(1);
        onHide();
    };

    const getStatusOptions = (tipoBanca: string) => {
        switch (tipoBanca) {
            case 'Facial':
                return [{ value: 'NAO_AVALIADO', label: 'Não avaliado' }];
            case 'Virtual':
                return [
                    { value: 'NAO_AVALIADO', label: 'Não avaliado' },
                    { value: 'AUSENTE', label: 'Ausente' },
                    { value: 'INCONCLUSIVO', label: 'Inconclusivo' },
                    { value: 'VALIDADO', label: 'Validado' }
                ];
            case 'Recursal':
                return [
                    { value: 'AUSENTE', label: 'Ausente' },
                    { value: 'NAO_VALIDADO', label: 'Não Validado' },
                    { value: 'VALIDADO', label: 'Validado' }
                ];
            default:
                return [];
        }
    };

    // Função para obter editais disponíveis (não selecionados ainda)
    const getEditaisDisponiveis = (values: CandidatoFormValues, currentIndex: number) => {
        const editaisSelecionados = values.candidatoEditais
            .filter((_, index) => index !== currentIndex)
            .map(edital => edital.editalId);
        
        return processo.editais.filter(edital => 
            !editaisSelecionados.includes(edital.id)
        );
    };

    const handleSubmit = async (values: CandidatoFormValues, { setSubmitting, resetForm, setFieldValue }: any) => {
        if (etapa === 1) {
            // Validação manual de documentos
            const hasCandidateDocument = (values.cpf && values.cpf.trim() !== '') || (values.passaporte && values.passaporte.trim() !== '');
            const hasResponsibleDocument = values.showResponsavel && values.responsavel ? 
                ((values.responsavel.cpf && values.responsavel.cpf.trim() !== '') || 
                 (values.responsavel.passaporte && values.responsavel.passaporte.trim() !== '')) : false;

            if (!hasCandidateDocument && !hasResponsibleDocument) {
                toast.error('Pelo menos um documento deve ser informado (CPF ou Passaporte do candidato ou responsável)');
                setSubmitting(false);
                return;
            }
            
            setEtapa(2);
            setSubmitting(false);
            return;
        }

        // Etapa 2: Enviar para o backend
        try {

            // Preparar payload conforme o DTO do backend
            const payload: CandidatoFormValues = {
                candidatoEditais: values.candidatoEditais.map(edital => {
                    const editalSelecionado = processo.editais.find(e => e.id === edital.editalId);
                    return {
                        editalId: edital.editalId,
                        inscricao: edital.inscricao,
                        nomeEdital: editalSelecionado?.nome || ''
                    };
                }),
                nome: values.nome,
                email: values.email,
                status: values.status || 'NAO_AVALIADO',
            };

            // Campos opcionais
            if (values.nomeSocial && values.nomeSocial.trim() !== '') payload.nomeSocial = values.nomeSocial;
            if (values.cpf && values.cpf.trim() !== '') payload.cpf = values.cpf;
            if (values.passaporte && values.passaporte.trim() !== '') payload.passaporte = values.passaporte;
            if (values.autodeclaracaoRacial && values.autodeclaracaoRacial.trim() !== '') payload.autodeclaracaoRacial = values.autodeclaracaoRacial;
            if (values.presidenteBanca && values.presidenteBanca.trim() !== '') payload.presidenteBanca = values.presidenteBanca;

            // Responsável
            if (values.showResponsavel && values.responsavel && values.responsavel.nome) {
                payload.responsavel = {
                    nome: values.responsavel.nome,
                };
                
                if (values.responsavel.email && values.responsavel.email.trim() !== '') {
                    payload.responsavel.email = values.responsavel.email;
                }

                if (values.responsavel.nomeSocial && values.responsavel.nomeSocial.trim() !== '') {
                    payload.responsavel.nomeSocial = values.responsavel.nomeSocial;
                }
                if (values.responsavel.cpf && values.responsavel.cpf.trim() !== '') {
                    payload.responsavel.cpf = values.responsavel.cpf;
                }
                if (values.responsavel.passaporte && values.responsavel.passaporte.trim() !== '') {
                    payload.responsavel.passaporte = values.responsavel.passaporte;
                }
            }

            console.log('Payload sendo enviado:', payload);

            // Chamada à API
            const response = await api.post(`/processos/${processoId}/candidatos`, payload);
            const novoCandidato = response.data;

            resetForm();
            setEtapa(1);
            onHide();

            if (onCandidatoCriado) {
                onCandidatoCriado(novoCandidato, true);
            }
        } catch (error: any) {
            console.error('Erro ao adicionar candidato:', error);
            toast.error(BackendErrorMessage(error))
        } finally {
            setSubmitting(false);
        }
    };

    const addEdital = (values: CandidatoFormValues, setFieldValue: any) => {
        const newEditais = [...values.candidatoEditais, { editalId: '', inscricao: '', nomeEdital: '' }];
        setFieldValue('candidatoEditais', newEditais);
    };

    const removeEdital = (index: number, values: CandidatoFormValues, setFieldValue: any) => {
        if (values.candidatoEditais.length > 1) {
            const newEditais = values.candidatoEditais.filter((_, i) => i !== index);
            setFieldValue('candidatoEditais', newEditais);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>Adicionar Novo Candidato - Etapa {etapa} de 2</Modal.Title>
            </Modal.Header>
            <Formik
                initialValues={initialValues}
                validationSchema={etapa === 1 ? candidatoSchemaEtapa1 : candidatoSchemaEtapa2}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ handleSubmit, isSubmitting, values, setFieldValue, errors }) => (
                    <Form onSubmit={handleSubmit} noValidate>
                        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                            {etapa === 1 ? (
                            <>
                            <h6 className="mb-2">Editais e Inscrições</h6>

                            {values.candidatoEditais.map((edital, index) => {
                                const editaisDisponiveis = getEditaisDisponiveis(values, index);
                                const editalSelecionado = processo.editais.find(e => e.id === edital.editalId);
                                
                                // Incluir o edital selecionado na lista se ele existir
                                const editaisParaExibir = editalSelecionado && !editaisDisponiveis.find(e => e.id === editalSelecionado.id)
                                    ? [editalSelecionado, ...editaisDisponiveis]
                                    : editaisDisponiveis;
                                
                                return (
                                    <Row key={index} className="g-2 mb-2 align-items-end">
                                        <Col md={6}>
                                            <Form.Label className="small mb-1">Edital *</Form.Label>
                                            <Field
                                                as={Form.Select}
                                                name={`candidatoEditais[${index}].editalId`}
                                                size="sm"
                                            >
                                                <option value="">Selecione um edital</option>
                                                {editaisParaExibir.map((edital) => (
                                                    <option key={edital.id} value={edital.id}>
                                                        {edital.nome}
                                                    </option>
                                                ))}
                                            </Field>
                                            <ErrorMessage 
                                                name={`candidatoEditais[${index}].editalId`} 
                                                component="div" 
                                                className="text-danger small" 
                                            />
                                        </Col>
                                        <Col md={4}>
                                            <Form.Label className="small mb-1">Inscrição *</Form.Label>
                                            <Field
                                                as={Form.Control}
                                                type="text"
                                                name={`candidatoEditais[${index}].inscricao`}
                                                placeholder="Nº inscrição"
                                                size="sm"
                                            />
                                            <ErrorMessage 
                                                name={`candidatoEditais[${index}].inscricao`} 
                                                component="div" 
                                                className="text-danger small" 
                                            />
                                        </Col>
                                        <Col md={2}>
                                            {values.candidatoEditais.length > 1 && (
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => removeEdital(index, values, setFieldValue)}
                                                    title="Remover edital"
                                                >
                                                    ×
                                                </Button>
                                            )}
                                        </Col>
                                    </Row>
                                );
                            })}

                            <div className="mb-3">
                                {getEditaisDisponiveis(values, -1).length > 0 ? (
                                    <Button
                                        variant="link"
                                        size="sm"
                                        onClick={() => addEdital(values, setFieldValue)}
                                        className="p-0 text-decoration-none"
                                    >
                                        + Adicionar outro edital
                                    </Button>
                                ) : (
                                    <small className="text-muted">Todos os editais disponíveis já foram adicionados</small>
                                )}
                            </div>

                            <hr className="my-3" />

                            <h6 className="mb-2">Dados do Candidato</h6>

                            <Row className="g-2">
                                <Col md={8}>
                                    <Form.Group className="mb-2">
                                        <Form.Label className="small">Nome Completo *</Form.Label>
                                        <Field
                                            as={Form.Control}
                                            type="text"
                                            name="nome"
                                            placeholder="Nome completo do candidato"
                                            size="sm"
                                        />
                                        <ErrorMessage name="nome" component="div" className="text-danger small" />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-2">
                                        <Form.Label className="small">Nome Social</Form.Label>
                                        <Field
                                            as={Form.Control}
                                            type="text"
                                            name="nomeSocial"
                                            placeholder="Opcional"
                                            size="sm"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row className="g-2">
                                <Col md={6}>
                                    <Form.Group className="mb-2">
                                        <Form.Label className="small">CPF</Form.Label>
                                        <Field
                                            as={Form.Control}
                                            type="text"
                                            name="cpf"
                                            placeholder="123.456.789-09"
                                            size="sm"
                                        />
                                        <ErrorMessage name="cpf" component="div" className="text-danger small" />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-2">
                                        <Form.Label className="small">Passaporte</Form.Label>
                                        <Field
                                            as={Form.Control}
                                            type="text"
                                            name="passaporte"
                                            placeholder="AB123456"
                                            size="sm"
                                        />
                                        <ErrorMessage name="passaporte" component="div" className="text-danger small" />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Form.Group className="mb-2">
                                <Form.Label className="small">E-mail</Form.Label>
                                <Field
                                    as={Form.Control}
                                    type="email"
                                    name="email"
                                    placeholder="fulano@email.com"
                                    size="sm"
                                />
                                <ErrorMessage name="email" component="div" className="text-danger small" />
                            </Form.Group>

                            <Form.Group className="mb-2">
                                <Form.Label className="small">Autodeclaração Racial</Form.Label>
                                <Field as={Form.Select} name="autodeclaracaoRacial" size="sm">
                                    <option value="">Não declarado</option>
                                    <option value="PRETO">Preto</option>
                                    <option value="PARDO">Pardo</option>
                                    <option value="INDIGENA">Indígena</option>
                                    <option value="BRANCO">Branco</option>
                                    <option value="AMARELO">Amarelo</option>
                                    <option value="NAO_DECLARADO">Não declarado (explícito)</option>
                                </Field>
                            </Form.Group>

                            <Form.Group className="mb-2">
                                <Form.Check
                                    type="switch"
                                    id="has-responsavel"
                                    label="Possui responsável legal?"
                                    checked={values.showResponsavel}
                                    onChange={() => {
                                        const newValue = !values.showResponsavel;
                                        setFieldValue('showResponsavel', newValue);
                                        if (newValue) {
                                            setFieldValue('responsavel', {
                                                nome: '',
                                                nomeSocial: '',
                                                cpf: '',
                                                passaporte: '',
                                                email: ''
                                            });
                                        } else {
                                            setFieldValue('responsavel', undefined);
                                        }
                                    }}
                                    className="small"
                                />
                            </Form.Group>

                            {values.showResponsavel && (
                                <>
                                    <hr className="my-2" />
                                    <h6 className="mb-2">Dados do Responsável Legal</h6>

                                    <Row className="g-2">
                                        <Col md={8}>
                                            <Form.Group className="mb-2">
                                                <Form.Label className="small">Nome Completo *</Form.Label>
                                                <Field
                                                    as={Form.Control}
                                                    type="text"
                                                    name="responsavel.nome"
                                                    placeholder="Nome completo do responsável"
                                                    size="sm"
                                                />
                                                <ErrorMessage name="responsavel.nome" component="div" className="text-danger small" />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-2">
                                                <Form.Label className="small">Nome Social</Form.Label>
                                                <Field
                                                    as={Form.Control}
                                                    type="text"
                                                    name="responsavel.nomeSocial"
                                                    placeholder="Opcional"
                                                    size="sm"
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Row className="g-2">
                                        <Col md={6}>
                                            <Form.Group className="mb-2">
                                                <Form.Label className="small">CPF</Form.Label>
                                                <Field
                                                    as={Form.Control}
                                                    type="text"
                                                    name="responsavel.cpf"
                                                    placeholder="987.654.321-00"
                                                    size="sm"
                                                />
                                                <ErrorMessage name="responsavel.cpf" component="div" className="text-danger small" />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-2">
                                                <Form.Label className="small">Passaporte</Form.Label>
                                                <Field
                                                    as={Form.Control}
                                                    type="text"
                                                    name="responsavel.passaporte"
                                                    placeholder="XY987654"
                                                    size="sm"
                                                />
                                                <ErrorMessage name="responsavel.passaporte" component="div" className="text-danger small" />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Form.Group className="mb-2">
                                        <Form.Label className="small">E-mail</Form.Label>
                                        <Field
                                            as={Form.Control}
                                            type="email"
                                            name="responsavel.email"
                                            placeholder="responsavel@email.com"
                                            size="sm"
                                        />
                                        <ErrorMessage name="responsavel.email" component="div" className="text-danger small" />
                                    </Form.Group>
                                </>
                            )}
                            </>
                            ) : (
                                <>
                                    <h6 className="mb-3">Tipo da próxima banca de Heteroidentificação que este Candidato participará</h6>
                                    
                                    <Row className="g-3 mb-4">
                                        {['Facial', 'Virtual', 'Recursal'].map((tipo) => (
                                            <Col md={4} key={tipo}>
                                                <div
                                                    onClick={() => {
                                                        setFieldValue('tipoBanca', tipo);
                                                        const opcoes = getStatusOptions(tipo);
                                                        if (opcoes.length === 1) {
                                                            setFieldValue('status', opcoes[0].value);
                                                        } else {
                                                            setFieldValue('status', '');
                                                        }
                                                    }}
                                                    style={{
                                                        cursor: 'pointer',
                                                        border: values.tipoBanca === tipo ? '2px solid #0d6efd' : '1px solid #dee2e6',
                                                        borderRadius: '8px',
                                                        padding: '20px',
                                                        textAlign: 'center',
                                                        backgroundColor: values.tipoBanca === tipo ? '#e7f1ff' : 'white',
                                                        transition: 'all 0.2s'
                                                    }}
                                                >
                                                    <h5 style={{ marginBottom: '8px', color: values.tipoBanca === tipo ? '#0d6efd' : '#212529' }}>
                                                        {tipo}
                                                    </h5>
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>
                                    <ErrorMessage name="tipoBanca" component="div" className="text-danger small mb-3" />

                                    {values.tipoBanca && (
                                        <Form.Group className="mb-3">
                                            <Form.Label>Status do candidato *</Form.Label>
                                            {values.tipoBanca === 'Facial' ? (
                                                <Form.Control
                                                    type="text"
                                                    value="Não avaliado"
                                                    disabled
                                                    readOnly
                                                />
                                            ) : (
                                                <Field as={Form.Select} name="status">
                                                    <option value="">Selecione o status</option>
                                                    {getStatusOptions(values.tipoBanca).map(opt => (
                                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                    ))}
                                                </Field>
                                            )}
                                            <ErrorMessage name="status" component="div" className="text-danger small" />
                                        </Form.Group>
                                    )}
                                </>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            {etapa === 2 && (
                                <Button variant="outline-secondary" size="sm" onClick={() => setEtapa(1)}>
                                    Voltar
                                </Button>
                            )}
                            <Button variant="secondary" size="sm" onClick={handleClose}>
                                Cancelar
                            </Button>
                            <Button
                                variant="success"
                                type="submit"
                                disabled={isSubmitting}
                                size="sm"
                            >
                                {isSubmitting ? 'Adicionando...' : (etapa === 1 ? 'Próximo' : 'Adicionar')}
                            </Button>
                        </Modal.Footer>
                    </Form>
                )}
            </Formik>
        </Modal>
    );
};

export default AdicionarCandidatoModal;
